<template>
    <div v-if="loading" class="state-changed w-full h-20 fixed border-0 top-0 left-0 z-50"></div>   
    <ToastNotification
        :message="toastMessage"
        :background-class="toastBackgroundClass"
        @closed="toastMessage = ''"
    />

    <div class="max-w-5xl mb-4 relative">
        <router-link :to="'../Settings'">
            <font-awesome-icon icon="fas fa-arrow-left" class="absolute float-left text-lg p-2 text-button dark:text-dark.textOnBkg -mt-2" />
        </router-link>
        <h2 class="mt-2 text-xl font-semibold text-center mb-5">{{ $t('company_settings.headline') }}</h2>
        
        <section class="grid sm:grid-cols-2">
            <div class="sm:border-r border-sepBorder dark:border-dark.sepBorder pr-10">
                <div v-if="branchName">
                    <h2 class="text-sm mb-2 font-semibold">{{ $t('profile_details') }}</h2>
                    <div class="flex items-center">
                        <MazInput
                            v-model="branchName"
                            :label="$t('reg_form.agency_name')"
                        />
                        <MazBtn @click="save_profile_changes()" size="sm" class="ml-2">{{ $t('save') }}</MazBtn>
                    </div>
                </div>
                <div v-for="(checkbox, chI) in checkboxes" :key="chI" class="mt-2">
                    <slot v-if="chI == 'session_names'"></slot>
                    <slot v-else-if="chI == 'timezone_shift'">
                        <hr class="py-1 mb-3 border-0 border-b border-sepBorder dark:border-dark.sepBorder"/>
                        <label class="text-sm font-bold">{{checkbox.title}} {{ $t('company_settings.p[6]') }}</label><br />
                        <div class="my-1 flex">
                            <div class="mt-1 mr-2">{{ $t('company_settings.gmt') }}</div> 
                            <MazInput type="number" size="xs" v-model="checkbox.value" :min="-12" :max="12" />
                        </div>
                        <small class="text-xs">{{ $t('company_settings.p[0]') }}<br />{{ $t('company_settings.p[1]') }}<br />{{ $t('company_settings.p[2]') }}</small>
                        <br />
                        <small class="font-bold">{{ $t('company_settings.p[3]') }}</small>
                    </slot>
                </div>
            </div>
            <div class="sm:pl-10 w-full mt-10 sm:mt-0 w-full" v-if="!loading">
                <h2 class="text-gl font-semibold mb-2">{{ $t('session_management') }} {{ $t('Settings') }}</h2>
                <div v-for="(name, index) in sessions" :key="index" class="flex justify-between items-center mb-2 p-2 px-4 rounded-full border border-sepBorder dark:border-dark.sepBorder text-black dark:text-dark.textOnBkg cursor-pointer">
                    <slot v-if="session_editing_index == index">
                        <MazInput size="sm" v-model="sessions[index]" :label="$t('session_name')" @keyup.enter="updateSessionName(index)" />
                        <font-awesome-icon icon="fas fa-check" class="text-growth cursor-pointer" @click="updateSessionName(index)" />
                    </slot>
                    <slot v-else>
                        <label>{{ name }}</label>
                        <div class="flex items-center gap-3">
                            <font-awesome-icon icon="fas fa-pen" class="cursor-pointer" @click="session_editing_index = index" />
                            <font-awesome-icon v-if="index > 3" icon="fas fa-trash-can" class="cursor-pointer text-decline" @click="removeOneSession(index)" />
                        </div>
                    </slot>
                </div>
                <div class="text-center p-2 rounded-full border border-growth text-growth cursor-pointer font-semibold" @click="addNewSession()">
                    <font-awesome-icon icon="fas fa-plus" /> {{ $t('add_new') }}
                </div>
            </div>
            <div v-else class="flex justify-center items-center col-span-2">
                <font-awesome-icon icon="fas fa-spinner fa-spin" class="text-textOnBkg dark:text-dark.textOnBkg text-xl" />
            </div>

            <div class="flex justify-end sm:col-span-2 mt-5">
                <MazBtn @click="saveSettings()" size="sm">{{ $t('save_changes') }}</MazBtn>
            </div>
        </section>
    </div>
    
</template>

<script>
    import { useAuthStore } from '@/stores/auth';
    import { api_get_call, api_post_call } from '@/api/api.js';
    import ToastNotification from '@/components/ToastNotification.vue';
    export default {
        components: {
            ToastNotification
        },
        data() {
            return {
                loading: false,
                checkboxes: {},
                session_editing_index: null,
                sessions: [],
                branchName: '',
                toastMessage: '',
                toastBackgroundClass: 'bg-green-500',
            }
        },
        computed: {
            authStore() {
                return useAuthStore();
            },
            myself() {
                return this.authStore.myself;
            }
        },
        methods: {
            updateSessionName(index) {
                if (this.sessions[index].length > 0) {
                    this.saveSettings();
                    this.session_editing_index = null
                }
            },
            removeOneSession(index) {
                //remove a session from the sessions array
                if (confirm(this.$t('delete_session_confirm'))) {
                    this.sessions.splice(index, 1);
                }
            },
            addNewSession() {
                //add a new session to the sessions array
                this.sessions.push('');
                this.session_editing_index = this.sessions.length - 1;
            },
            checkIcon(condition) {
                return condition == 1 ? 'fas fa-check-square' : 'fas fa-square';
            },
            async save_profile_changes() {
                try {
                    this.loading = true;
                    let response = await api_post_call(`users/`, {
                        action: 'update_profile',
                        companyID: this.$cookies.get('c_id'),
                        userToken: this.$cookies.get('userToken'),
                        branchName: this.branchName
                    });
                    this.toastMessage = 'Saved';
                    this.toastBackgroundClass = 'bg-green-500';
                } catch (error) {
                    this.toastMessage = 'Error';
                    this.toastBackgroundClass = 'bg-decline';
                } finally {
                    this.loading = false;
                }
            },
            async saveSettings() {
                let response = await api_post_call(`settingspost/`, {
                    action: 'companySettings',
                    checkboxes: this.checkboxes,
                    session_names: this.sessions,
                    userToken: this.$cookies.get('userToken'),
                    companyID: this.$cookies.get('c_id')
                });
                if (response) {
                    this.toastMessage = 'Saved';
                    this.toastBackgroundClass = 'bg-green-500';
                }
            }
        },
        async created() {
            let response = await api_get_call(`settingsget/?action=companySettings&userToken=${this.$cookies.get('userToken')}&companyID=${this.$cookies.get('c_id')}`);
            if (response) {
                this.checkboxes = response;
                this.sessions = this.checkboxes.session_names.value;
                console.log(this.checkboxes)
            }
            this.branchName = this.myself.branchName;
        }
    }
</script>